/**
 * When a task is due and whether it is overdue: the one rule the task lists, the task page and
 * What's Next all use.
 *
 * A task's start date (`when`) and due date are both optional; with both, it starts on or before
 * the day it is due.
 *
 * - A task with a due date is not overdue until its due date has passed.
 * - A task without one is overdue once its start date has passed.
 *
 * A task can run over several days, from its start date to its due date. Until it is due it
 * belongs with today's tasks, not with the overdue ones. A task with a due date and no start date
 * belongs with today's tasks on the day it is due.
 */
/**
 * Whether the task is waiting on a prerequisite: a goal plan task whose earlier tasks are not done.
 * It is shown by its dates like any other task, marked as waiting.
 */
export function isTaskWaiting(task) {
    return !task.completedDate && (task.blockedBy?.length ?? 0) > 0;
}
/**
 * The day the task is first shown on: its start date, otherwise its due date. A task with only a
 * due date waits under Upcoming until that day, never under Unscheduled.
 */
export function taskStartDay(task) {
    return task.when || task.dueDate || undefined;
}
/** The day the task is due: its due date, otherwise its start date. */
export function taskDeadline(task) {
    return task.dueDate || task.when || undefined;
}
/**
 * Incomplete, and its due date (or, without one, its start date) is before `today`. A waiting task
 * can be overdue too: its plan is running late.
 */
export function isTaskOverdue(task, today) {
    if (task.completedDate)
        return false;
    const deadline = taskDeadline(task);
    return deadline !== undefined && deadline < today;
}
/**
 * Whether the task belongs with today's tasks: `today` falls between its start date and the day it
 * is due, both included. With only a start date that is its start day; with only a due date, its
 * due day. Completion does not matter here, so a task done today still shows with today's tasks.
 */
export function isTaskInTodayWindow(task, today) {
    const start = taskStartDay(task);
    const deadline = taskDeadline(task);
    return !!start && !!deadline && start <= today && today <= deadline;
}
/**
 * The due date a task keeps once its start date changes to `when`: unchanged, unless the task now
 * starts after it, which clears it. The server applies the same rule (`dueDateForSchedule` in
 * @zendia/domain); this copy keeps an optimistic update in step with it.
 */
export function dueDateForSchedule(when, dueDate) {
    if (!dueDate || (when && dueDate < when))
        return undefined;
    return dueDate;
}
/** How many days after today the Upcoming section reaches, as on the server. */
export const TASK_UPCOMING_DAYS = 7;
/** `day` (YYYY-MM-DD) moved by `days`. */
const addDays = (day, days) => {
    const date = new Date(`${day}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
};
/**
 * Whether the task belongs in a section of the dashboard Tasks panel, as of `today` - the same
 * rules the server selects each section's pages by (`section` on GET /tasks/paged), so a task
 * changed here can leave its section before the refetch confirms it. A task can be in two
 * sections when it starts after its due date: overdue by its due date, upcoming by its start.
 * A completed task is only ever in Today, by the day it was completed, whatever its schedule:
 * today, or from `completedFrom` through today.
 */
export function isTaskInSection(task, section, today, completedFrom = today) {
    if (task.completedDate) {
        return section === 'today' && task.completedDate >= completedFrom && task.completedDate <= today;
    }
    switch (section) {
        case 'today':
            return isTaskInTodayWindow(task, today);
        case 'overdue':
            return isTaskOverdue(task, today);
        case 'upcoming': {
            const start = taskStartDay(task);
            return !!start && start >= addDays(today, 1) && start <= addDays(today, TASK_UPCOMING_DAYS);
        }
        case 'unscheduled':
            return !taskStartDay(task);
    }
}
